import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "../../hooks/use-toast";
import axios from '../../lib/axios';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Alert,
  AlertDescription,
} from "../../components/ui/alert";
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import SearchSelectCompanies from '@/components/SearchSelectCompanies/SearchSelectCompanies';

const UsersCreatePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/users', formData);
      
      toast({
        title: "Success",
        description: "User created successfully",
      });
      
      navigate(`/users/${response.data.id}`);
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.response?.data?.error === 'User with this email already exists') {
        setErrors(prev => ({
          ...prev,
          email: 'Email is already in use'
        }));
      } else {
        toast({
          title: "Error",
          description: error?.response?.data?.error?.message || "Failed to create user. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (value) => {
    setFormData(prev => ({
        ...prev,
        company_id: value[0]?.id,
        company: {
            id: value[0]?.id,
            name: value[0]?.name,
        },
      }));
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      role: value
    }));
    if (errors.role) {
      setErrors(prev => ({
        ...prev,
        role: ''
      }));
    }
  };

  return (
    <div className="min-h-full min-w-full flex flex-col">
      <Card className="w-full max-w-7xl mx-auto">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/users')}
              className="flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Users
            </Button>
          </div>
          <CardTitle className="text-3xl font-bold text-center">Create User</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Enter first name"
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.first_name}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="first_name">Last Name</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Enter last name"
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.last_name}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.email}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger className={errors.role ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
              {errors.role && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription>{errors.role}</AlertDescription>
                </Alert>
              )}
            </div>


          {formData.role !== 'admin' && 
            <div className="space-y-2">
                <Label htmlFor="company_id">Company</Label>
                <SearchSelectCompanies isSingle values={ formData.company_id ? [{id: formData.company_id, name:  formData?.company?.name} ] : []} onChange={handleCompanyChange}/>
                {errors.company_id && (
                <Alert variant="destructive" className="py-2">
                    <AlertDescription>{errors.company_id}</AlertDescription>
                </Alert>
                )}
            </div>
          }

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send User Email'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersCreatePage;